import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TierManagement = ({ formData, onFormChange, onNext, onPrevious }) => {
  const [tiers, setTiers] = useState(formData?.tiers || [
    {
      id: 1,
      name: 'General Admission',
      price: '',
      capacity: '',
      description: '',
      benefits: [],
      earlyBird: false,
      earlyBirdPrice: '',
      earlyBirdEndDate: ''
    }
  ]);

  const benefitOptions = [
    'Access to all sessions',
    'Welcome kit',
    'Networking lunch',
    'Certificate of attendance',
    'Recording access',
    'Priority seating',
    'Meet & greet with speakers',
    'Exclusive workshop access',
    'VIP lounge access',
    'Complimentary refreshments'
  ];

  const addTier = () => {
    const newTier = {
      id: Date.now(),
      name: '',
      price: '',
      capacity: '',
      description: '',
      benefits: [],
      earlyBird: false,
      earlyBirdPrice: '',
      earlyBirdEndDate: ''
    };
    const updatedTiers = [...tiers, newTier];
    setTiers(updatedTiers);
    onFormChange({ tiers: updatedTiers });
  };

  const removeTier = (tierId) => {
    const updatedTiers = tiers?.filter(tier => tier?.id !== tierId);
    setTiers(updatedTiers);
    onFormChange({ tiers: updatedTiers });
  };

  const updateTier = (tierId, field, value) => {
    const updatedTiers = tiers?.map(tier =>
      tier?.id === tierId ? { ...tier, [field]: value } : tier
    );
    setTiers(updatedTiers);
    onFormChange({ tiers: updatedTiers });
  };

  const updateTierBenefits = (tierId, benefit, checked) => {
    const tier = tiers?.find(t => t?.id === tierId);
    const updatedBenefits = checked
      ? [...tier?.benefits, benefit]
      : tier?.benefits?.filter(b => b !== benefit);
    
    updateTier(tierId, 'benefits', updatedBenefits);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onNext();
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Ticket Tiers & Pricing</h2>
        <p className="text-muted-foreground">Configure ticket types and pricing options</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {tiers?.map((tier, index) => (
          <div key={tier?.id} className="border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">
                Tier {index + 1}
              </h3>
              {tiers?.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTier(tier?.id)}
                  className="text-error hover:text-error"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input
                  label="Tier Name"
                  type="text"
                  placeholder="e.g., General Admission, VIP, Premium"
                  value={tier?.name}
                  onChange={(e) => updateTier(tier?.id, 'name', e?.target?.value)}
                  required
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Price (USD)"
                    type="number"
                    placeholder="0.00"
                    value={tier?.price}
                    onChange={(e) => updateTier(tier?.id, 'price', e?.target?.value)}
                    required
                    min="0"
                    step="0.01"
                  />
                  <Input
                    label="Available Tickets"
                    type="number"
                    placeholder="Enter quantity"
                    value={tier?.capacity}
                    onChange={(e) => updateTier(tier?.id, 'capacity', e?.target?.value)}
                    required
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tier Description
                  </label>
                  <textarea
                    value={tier?.description}
                    onChange={(e) => updateTier(tier?.id, 'description', e?.target?.value)}
                    placeholder="Describe what's included in this tier"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <Checkbox
                    label="Enable Early Bird Pricing"
                    checked={tier?.earlyBird}
                    onChange={(e) => updateTier(tier?.id, 'earlyBird', e?.target?.checked)}
                  />
                </div>

                {tier?.earlyBird && (
                  <div className="pl-6 space-y-4 border-l-2 border-primary">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Early Bird Price (USD)"
                        type="number"
                        placeholder="0.00"
                        value={tier?.earlyBirdPrice}
                        onChange={(e) => updateTier(tier?.id, 'earlyBirdPrice', e?.target?.value)}
                        required
                        min="0"
                        step="0.01"
                      />
                      <Input
                        label="Early Bird End Date"
                        type="date"
                        value={tier?.earlyBirdEndDate}
                        onChange={(e) => updateTier(tier?.id, 'earlyBirdEndDate', e?.target?.value)}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Included Benefits
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {benefitOptions?.map((benefit) => (
                    <Checkbox
                      key={benefit}
                      label={benefit}
                      checked={tier?.benefits?.includes(benefit)}
                      onChange={(e) => updateTierBenefits(tier?.id, benefit, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={addTier}
            iconName="Plus"
            iconPosition="left"
          >
            Add Another Tier
          </Button>
        </div>

        <div className="bg-muted p-4 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-2">Pricing Summary</h4>
          <div className="space-y-1">
            {tiers?.map((tier, index) => (
              <div key={tier?.id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {tier?.name || `Tier ${index + 1}`}
                </span>
                <span className="text-foreground font-medium">
                  {tier?.earlyBird && tier?.earlyBirdPrice 
                    ? `$${tier?.earlyBirdPrice} (Early) / $${tier?.price || '0.00'}`
                    : `$${tier?.price || '0.00'}`
                  }
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t border-border">
          <Button type="button" variant="outline" onClick={onPrevious} iconName="ArrowLeft">
            Back to Venue
          </Button>
          <Button type="submit" iconName="ArrowRight" iconPosition="right">
            Continue to Publishing
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TierManagement;